import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, adminLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "super_admin")) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await adminLogin(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome, admin.");
      navigate("/admin", { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] fl-grid-bg p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900/70 backdrop-blur-xl p-8 sm:p-10">
        <Logo size="h-8" className="mb-6" />
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#E53E3E]"><ShieldCheck className="w-6 h-6 text-white" /></span>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">FitLynk</p>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight leading-none">Admin Portal</h1>
          </div>
        </div>
        <p className="mt-5 text-sm text-zinc-400 flex items-center gap-2"><Lock className="w-4 h-4" /> Restricted access. Authorized staff only.</p>

        <form onSubmit={submit} className="mt-7 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-zinc-300">Email</Label>
            <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@fitlynk.com" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="admin-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-zinc-300">Password</Label>
            <Input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="admin-password-input" />
          </div>
          {error && <p className="text-sm text-[#E53E3E]" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="admin-login-submit-button">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Access Portal"}
          </Button>
        </form>
      </div>
    </div>
  );
}
